import React, { useContext } from 'react';

import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { RentalRedirector } from '@thd-nucleus/app-render';

import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { LayoutManager, LayoutGroup } from '@thd-olt-component-react/layout-manager';
import { MediaGallery } from '@thd-olt-component-react/media-gallery';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductOverview } from '@thd-olt-component-react/product-overview';
import { RentalPrice } from '@thd-olt-component-react/rental-price';
import { RentalFulfillment } from '@thd-olt-component-react/rental-fulfillment';
import { SalientPoints, SalientListItem } from '@thd-olt-component-react/salient-points';
import { Specifications } from '@thd-olt-component-react/specifications';
import { Metadata } from '@thd-olt-component-react/metadata';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { RentalBanners } from '../components/RentalBanners';
import { getConfigLists } from '../helpers';
import { ProductMetadata } from '../components/ProductMetadata';

import '../styles/Product.scss';

export const Product = () => {
  declareContexts([QueryContext]);

  const { channel, segment = '', isDebugMode } = useContext(ExperienceContext);
  const { itemId } = useParams();
  const isMobile = channel === 'mobile';

  const { blacklistedItems = [] } = getConfigLists();

  const history = useHistory();
  const { pathname } = useLocation();

  const showReserveNowButton = () => (
    !blacklistedItems.includes(itemId)
  );

  const handleStartReservationButtonClick = () => {
    if (itemId) {
      const reservationsPath = pathname.replace('/p/rental', '/p/rental/reservations');
      history.push(reservationsPath);
    }
  };

  return (
    <QueryProvider
      defaultVariables={{ dataSource: 'rental', fetchPolicy: 'cache-first', skipKPF: true }}
      cacheKey={`rental-product-${isMobile ? 'mobile' : 'desktop'}`}
    >
      <Row className="isBound">
        <LayoutManager
          name="rental-pip"
          input={{
            channel,
            itemId,
            isDebugMode,
            segment
          }}
          useContextForProps
        >
          <LayoutGroup name="redirector">
            <RentalRedirector />
          </LayoutGroup>
          <LayoutGroup name="product-metadata">
            <Metadata>
              <ProductMetadata itemId={itemId} />
            </Metadata>
          </LayoutGroup>
          <LayoutGroup sm="10" name="breadcrumbs">
            <Col>
              <Breadcrumbs
                itemId={itemId}
                showInternet={isMobile}
              />
            </Col>
          </LayoutGroup>
          <LayoutGroup name="zone-a">
            <Col nopadding={channel === 'desktop'}>
              <Row>
                <Col sm="7">
                  <LayoutManager name="buy-area">
                    <div className="component">
                      <LayoutManager name="product-details-share">
                        {isMobile && (
                          <LayoutGroup sm="12" name="product-details">
                            <ProductDetails itemId={itemId}>
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Badge itemId={itemId} />
                                <ProductDetails.Title />
                                <ProductDetails.BrandCollection showCollection />
                              </div>
                            </ProductDetails>
                          </LayoutGroup>
                        )}
                      </LayoutManager>
                      <LayoutGroup sm={12} name="media-gallery">
                        <Col>
                          <MediaGallery itemId={itemId} />
                        </Col>
                      </LayoutGroup>
                      { !isMobile && (
                        <LayoutGroup name="product-infobar">
                          <Row>
                            <Col className="u__text-align--center">
                              <ProductInfoBar itemId={itemId} />
                            </Col>
                          </Row>
                        </LayoutGroup>
                      )}
                    </div>
                  </LayoutManager>
                </Col>
                <Col sm="5">
                  <Row>
                    <LayoutManager name="buy-area" input={{ channel, segment }}>
                      <div className="component">
                        {!isMobile && (
                          <>
                            <LayoutGroup name="product-details">
                              <Col>
                                <ProductDetails itemId={itemId}>
                                  <div className="product-details__badge-title--wrapper">
                                    <ProductDetails.Badge itemId={itemId} />
                                    <ProductDetails.Title />
                                    <ProductDetails.BrandCollection showCollection />
                                  </div>
                                </ProductDetails>
                              </Col>
                            </LayoutGroup>
                            <LayoutGroup name="salient-points">
                              <Col>
                                <SalientPoints itemId={itemId} max={3}>
                                  <SalientListItem>
                                    <a className="u__default-link" href="#product-overview">See More Details</a>
                                  </SalientListItem>
                                </SalientPoints>
                              </Col>
                            </LayoutGroup>
                          </>
                        )}
                        <LayoutGroup name="rental-price">
                          <Col>
                            <RentalPrice itemId={itemId} />
                          </Col>
                        </LayoutGroup>
                        <LayoutGroup name="rental-fulfillment">
                          <Col>
                            <RentalFulfillment
                              itemId={itemId}
                              showReserveNowButton={showReserveNowButton()}
                              handleButtonClick={handleStartReservationButtonClick}
                            />
                          </Col>
                        </LayoutGroup>
                      </div>
                    </LayoutManager>
                  </Row>
                </Col>
              </Row>
            </Col>
          </LayoutGroup>
          <LayoutGroup name="product-overview" id="product-overview">
            <div id="product-overview" className="component">
              <Col>
                <ProductOverview itemId={itemId} channel={channel} />
              </Col>
            </div>
          </LayoutGroup>
          <LayoutGroup name="specifications">
            <div className="component">
              <Col>
                <Specifications itemId={itemId} />
              </Col>
            </div>
          </LayoutGroup>
          <LayoutGroup name="rental-banners">
            <div className="component">
              <Col>
                <RentalBanners />
              </Col>
            </div>
          </LayoutGroup>
          <EmtLinks
            path={pathname}
            layoutGroup={{
              tag: Row
            }}
            dynamic={{
              pageType: 'rental-pip'
            }}
            hydrator={{
              className: 'grid'
            }}
          />
        </LayoutManager>
      </Row>
    </QueryProvider>
  );
};

Product.displayName = 'RentalProductPage';
Product.propTypes = {};
Product.defaultProps = {};